import { Badge, FormGroup, Label } from "@xola/ui-kit";
import React, { ReactNode } from "react";
import { BaseSchedule, BaseScheduleRepeat, Weekday, WeekdayCopy } from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { CustomDates, DateRange } from "../Common/ScheduleSummary";
import { ScheduleTimeRanges } from "../Common/ScheduleTimeRanges";

interface BaseScheduleSummaryProps {
    schedule: BaseSchedule;
    showDetails?: boolean;
    productStyling?: boolean;
    children?: ReactNode;
}

export const BaseScheduleSummary = ({
    schedule,
    showDetails = true,
    productStyling = false,
    children,
}: BaseScheduleSummaryProps) => {
    const shouldShowDaysInSchedule =
        showDetails && schedule.days && schedule.days.length > 0 && schedule.repeat !== BaseScheduleRepeat.CUSTOM;

    return (
        <div className="w-full">
            <div className="flex justify-between">
                <NoTranslate as="div" className="pb-4 text-md font-semibold">
                    {schedule?.name?.toUpperCase() ?? ""}
                </NoTranslate>
                {children}
            </div>

            {schedule.repeat === BaseScheduleRepeat.WEEKLY ? (
                <DateRange start={schedule.start} end={schedule.end} />
            ) : schedule.repeat === BaseScheduleRepeat.CUSTOM ? (
                <CustomDates dates={schedule.dates} />
            ) : null}

            {shouldShowDaysInSchedule ? (
                <FormGroup>
                    <Label>Days of Week</Label>

                    {productStyling ? (
                        <div className="space-x-1" data-testid="base-schedule-summary-days-of-week">
                            {Object.keys(WeekdayCopy).map((_day, index) => {
                                const weekdayIndex = index as unknown as Weekday;
                                return schedule.days?.includes(index) ? (
                                    <Badge key={weekdayIndex}>{WeekdayCopy[weekdayIndex]}</Badge>
                                ) : (
                                    <InactiveBadge key={weekdayIndex}>{WeekdayCopy[weekdayIndex]}</InactiveBadge>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-gray-darker" data-testid="base-schedule-summary-days-of-week">
                            {schedule.days?.map((day) => WeekdayCopy[day]).join(", ")}
                        </div>
                    )}
                </FormGroup>
            ) : null}

            {showDetails && schedule.timeRanges?.length ? (
                <FormGroup>
                    <Label>Time Ranges</Label>

                    <div
                        className="flex items-center space-x-2 text-gray-darker"
                        data-testid="base-schedule-summary-time-range"
                    >
                        <ScheduleTimeRanges productStyling timeRanges={schedule.timeRanges} />
                    </div>
                </FormGroup>
            ) : null}
        </div>
    );
};

const InactiveBadge = ({ children }: { children: string }) => (
    <Badge color="secondary" className="opacity-50">
        {children}
    </Badge>
);
